import React, { useState, useContext, useEffect, useRef } from "react";
import { Container, Button, Table, Modal, ModalHeader, ModalBody, Form, FormGroup } from "reactstrap";
import { SearchSelect, SearchSelectItem } from "@tremor/react";
import { AppContext } from "../application/provider";
import ProspectoService from "../axios_services/prospectos.service";
import UsuariosService from "../axios_services/usuarios.service";
import Pagination from "../components/Pagination/Pagination";
import Loader from "../components/Loader/Loader";
import ButtonUpdate from "../components/Buttons/ButtonUpdate";
import "./Prospectos.css";
import "./RestablecerProspectos.css";

const PERFILES_PERMITIDOS = [6, 8, 23];

/** Celular con dropdown desplegable — igual a FilaProspecto.js */
const CelularesCell = ({ celulares }) => {
  const [open, setOpen] = useState(false);

  if (!celulares || celulares.length === 0) {
    return <span style={{ color: "#94a3b8", fontSize: "0.72rem", fontStyle: "italic" }}>SIN REGISTRO</span>;
  }

  const first = celulares[0];

  return (
    <div className="relative flex items-center justify-between min-w-[100px]">
      <a
        href={`https://wa.me/51${first}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[10px] text-gray-700 hover:underline"
      >
        {first}
      </a>
      {celulares.length > 1 && (
        <div className="relative">
          <button
            onClick={() => setOpen(!open)}
            className="text-gray-500 hover:text-blue-600 focus:outline-none p-0.5 rounded transition-colors ml-1"
            type="button"
          >
            <svg
              className={`w-3 h-3 transform transition-transform ${open ? "rotate-180" : ""}`}
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 9-7 7-7-7" />
            </svg>
          </button>
          {open && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
              <div className="absolute z-50 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg w-32 left-0 text-left">
                <ul className="py-1 text-[10px] text-gray-700">
                  {celulares.map((cel, i) => (
                    <li key={i}>
                      <a
                        href={`https://wa.me/51${cel}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full px-2 py-1.5 hover:bg-gray-100 text-left text-gray-700 uppercase"
                        onClick={() => setOpen(false)}
                      >
                        {cel}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};


function RestablecerProspectos() {
  const [state] = useContext(AppContext);

  // Filtros
  const [dni, setDni] = useState("");
  const [nombre, setNombre] = useState("");
  const [razon_social_id, setRazonSocialId] = useState("");

  // Tabla y paginación
  const [prospectos, setProspectos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rows_per_page, setRowsPerPage] = useState(10);
  const [loadingTable, setLoadingTable] = useState(false);

  // Modal de restablecimiento
  const [modalOpen, setModalOpen] = useState(false);
  const [prospectoSeleccionado, setProspectoSeleccionado] = useState(null);
  const [gestorSeleccionado, setGestorSeleccionado] = useState("");
  const [gestoresDisponibles, setGestoresDisponibles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasFetched = useRef(false);

  const instituciones = state.catalogos?.instituciones || [];

  // Carga inicial automática
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchProspectos(1, 10, "", "", "");
    fetchGestores();
  }, []);

  const fetchGestores = async () => {
    try {
      const data = await UsuariosService.getPersonal(state.user?.usuario_id, null, null, state.user?.perfil_id);
      if (Array.isArray(data)) {
        const filtrados = data.filter(u => Number(u.perfil_id) === 3 || (Number(u.perfil_id) === 4 || Number(u.perfil_id) === 27));
        setGestoresDisponibles(filtrados);
      }
    } catch (err) {
      console.error("Error al obtener gestores disponibles:", err);
    }
  };

  // Verificación de acceso
  if (!PERFILES_PERMITIDOS.includes(Number(state.user?.perfil_id))) {
    return (
      <Container className="prospectos">
        <div className="encabezado">
          <h1>RESTABLECER PROSPECTOS</h1>
        </div>
        <p style={{ color: "#ef4444", marginTop: "2rem", fontWeight: 600 }}>
          ⛔ No tiene permiso para acceder a este módulo.
        </p>
      </Container>
    );
  }

  const fetchProspectos = async (p, rpp, dniVal, nombreVal, razonVal) => {
    setLoadingTable(true);
    try {
      const data = await ProspectoService.getProspectosInactivos(
        state.user?.usuario_id,
        state.user?.perfil_id,
        dniVal,
        nombreVal,
        razonVal,
        rpp,
        p
      );
      const arr = Array.isArray(data) ? data : [];
      setProspectos(arr);
      setTotal(arr.length > 0 && arr[0]?.total_prospectos ? Number(arr[0].total_prospectos) : 0);
    } catch (err) {
      console.error("Error al obtener prospectos inactivos:", err);
      setProspectos([]);
      setTotal(0);
    } finally {
      setLoadingTable(false);
    }
  };

  const handleBuscar = async () => {
    setPage(1);
    await fetchProspectos(1, rows_per_page, dni, nombre, razon_social_id);
  };

  const handlePageChange = (newPage) => {
    const maxPage = Math.ceil(total / rows_per_page);
    if (newPage >= 1 && newPage <= maxPage) {
      setPage(newPage);
      fetchProspectos(newPage, rows_per_page, dni, nombre, razon_social_id);
    }
  };

  const handleRowsChange = (e) => {
    const rpp = Number(e.target.value);
    setRowsPerPage(rpp);
    setPage(1);
    fetchProspectos(1, rpp, dni, nombre, razon_social_id);
  };

  const abrirModal = (prospecto) => {
    setProspectoSeleccionado(prospecto);
    setGestorSeleccionado(prospecto.gestor_id ? String(prospecto.gestor_id) : "");
    setModalOpen(true);
  };

  const cerrarModal = () => {
    setModalOpen(false);
    setProspectoSeleccionado(null);
    setGestorSeleccionado("");
  };

  const handleRestablecer = async () => {
    if (!gestorSeleccionado) {
      alert("Debe seleccionar un gestor para restablecer el prospecto.");
      return;
    }
    setIsSubmitting(true);
    try {
      await ProspectoService.reactivarProspecto(
        prospectoSeleccionado.prospecto_id,
        state.user?.usuario_id,
        state.user?.perfil_id,
        Number(gestorSeleccionado)
      );
      alert(`Prospecto ${prospectoSeleccionado.nombre} restablecido exitosamente.`);
      cerrarModal();
      await fetchProspectos(page, rows_per_page, dni, nombre, razon_social_id);
    } catch (err) {
      const msg = err?.response?.data?.error || "Error al restablecer el prospecto.";
      alert(`❌ ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="prospectos">
      {/* Encabezado */}
      <div className="encabezado">
        <h1>RESTABLECER PROSPECTOS</h1>
      </div>

      {/* Filtros — misma estructura que LISTA DE PROSPECTOS */}
      <div className="filtros">
        <div>
          <label htmlFor="rp-dni">Ingrese DNI:</label>
          <input
            type="text"
            id="rp-dni"
            value={dni}
            onChange={(e) => setDni(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="rp-nombre">Ingrese nombre:</label>
          <input
            type="text"
            id="rp-nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="rp-razon-social">Razón social:</label>
          <SearchSelect
            id="rp-razon-social"
            className="search_select"
            value={razon_social_id}
            onValueChange={(v) => setRazonSocialId(v)}
            placeholder="Seleccione..."
          >
            {instituciones.map((inst) => (
              <SearchSelectItem
                key={inst.institucion_id}
                value={inst.institucion_id}
                className="search_select"
              >
                {inst.razon_social}
              </SearchSelectItem>
            ))}
          </SearchSelect>
        </div>
      </div>

      {/* Botón buscar */}
      <div>
        <Button className="buscar" onClick={handleBuscar} disabled={loadingTable}>
          Buscar
        </Button>
      </div>

      {/* Paginación + tabla — misma estructura que Prospectos */}
      <div style={{ position: "relative" }}>
        <Pagination
          page={page}
          totalItems={total}
          rowsPerPage={rows_per_page}
          handlePageChange={handlePageChange}
        />

        {/* Contador y selector de filas */}
        <div className="flex justify-between items-center mt-[18px] mb-4">
          <div>
            <label className="font-normal text-base text-gray-800">
              Total de prospectos inactivos:{" "}
              <span className="numero inline-flex items-center gap-1">
                <select
                  className="border border-gray-300 rounded px-2 py-[2px] text-sm bg-white text-gray-800 min-w-[70px]"
                  value={rows_per_page}
                  onChange={handleRowsChange}
                >
                  <option value={10}>10</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={1000}>1000</option>
                </select>
                <span className="whitespace-nowrap font-normal text-gray-800">{`de ${Number(total)}`}</span>
              </span>
            </label>
          </div>
        </div>

        {/* Contenedor de tabla con loading — idéntico a Prospectos */}
        <div className={`table-loading-container no-select ${loadingTable ? "loading" : "loaded"}`}>
          {loadingTable && (
            <div className="table-loading-overlay">
              <Loader />
            </div>
          )}

          <div className="table-container-prospectos">
            <div className="table-container">
              <Table hover size="sm">
                <thead>
                  <tr>
                    <th className="prospecto-dni">DNI</th>
                    <th className="prospecto-nombre">Apellidos y Nombres</th>
                    <th className="prospecto-celular">Celulares</th>
                    <th className="prospecto-convenio">Convenio</th>
                    <th className="prospecto-gestor-asignado">Gestor Original</th>
                    <th style={{ textAlign: "center" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {prospectos.length > 0 ? (
                    prospectos.map((p) => (
                      <tr key={p.prospecto_id}>
                        <td className="prospecto-dni">{p.dni}</td>
                        <td className="prospecto-nombre">{p.nombre}</td>
                        <td className="prospecto-celular">
                          <CelularesCell celulares={p.celulares} />
                        </td>
                        <td className="prospecto-convenio">
                          {p.razon_social || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>—</span>}
                        </td>
                        <td className="prospecto-gestor-asignado">
                          {p.gestor_nombre || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Sin gestor</span>}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                            <ButtonUpdate
                              onClick={() => abrirModal(p)}
                              title="Restablecer prospecto"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        style={{ textAlign: "center", padding: "2rem", color: "#94a3b8", fontStyle: "italic" }}
                      >
                        {loadingTable ? "" : "No se encontraron prospectos inactivos sin duplicado activo."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>


      {/* Modal de Restablecimiento */}
      {prospectoSeleccionado && (
        <Modal isOpen={modalOpen} className="custom-modal" backdrop={true}>
          <ModalHeader className="modal-header">
            <div className="titulo">
              <h1>RESTABLECER PROSPECTO</h1>
              <Button className="close-btn" onClick={cerrarModal}>X</Button>
            </div>
          </ModalHeader>
          <div className="modal-body-footer">
            <ModalBody className="modal-body">
              <Form className="fila">
                <FormGroup>
                  <label className="label">DNI:</label>
                  <input className="form-control font-mono font-semibold" value={prospectoSeleccionado.dni} disabled />
                </FormGroup>
                <FormGroup>
                  <label className="label">Nombre:</label>
                  <input className="form-control" value={prospectoSeleccionado.nombre} disabled />
                </FormGroup>
                <FormGroup>
                  <label className="label">Convenio:</label>
                  <input className="form-control" value={prospectoSeleccionado.razon_social || "—"} disabled />
                </FormGroup>
                <FormGroup>
                  <label className="label">
                    Asignar Gestor <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    className="form-control"
                    value={gestorSeleccionado}
                    onChange={(e) => setGestorSeleccionado(e.target.value)}
                  >
                    <option value="">Seleccionar gestor</option>
                    {gestoresDisponibles.map((u) => (
                      <option key={u.usuario_id} value={u.usuario_id}>
                        {u.nombre_completo_usuario?.toUpperCase()}
                      </option>
                    ))}
                  </select>
                </FormGroup>
              </Form>

              <div className="button-container">
                <Button
                  className="button-custom"
                  onClick={handleRestablecer}
                  disabled={isSubmitting || !gestorSeleccionado}
                >
                  {isSubmitting ? "Procesando..." : "GUARDAR"}
                </Button>
              </div>
            </ModalBody>

          </div>
        </Modal>
      )}
    </Container>
  );
}

export default RestablecerProspectos;
